import React from 'react'
import { StyleProp, ViewStyle } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import {
    type DashboardTileBorder,
    type DashboardTileStatus,
    dashboardTileVariants,
} from './variants'

export interface DashboardTileProps {
    status?: DashboardTileStatus
    border?: DashboardTileBorder
    /** Applies hover styling (web grid cells). */
    interactive?: boolean
    /** Warning-reasons state: fills the card with the status colour. */
    filled?: boolean
    /** Platform title bar — web adds resize/help controls, mobile a status dot. */
    title?: React.ReactNode
    footer?: React.ReactNode
    /** Status-reveal / alert overlay, positioned over the card by the platform. */
    overlay?: React.ReactNode
    /** Classes for the content area between title and footer. */
    contentClassName?: string
    className?: string
    style?: StyleProp<ViewStyle>
    testID?: string
    children?: React.ReactNode
}

/**
 * Presentational card shell shared by the web dashboard grid cell and the mobile
 * dashboard tile: surface, radius, status border, and slots. It holds no state
 * and no behaviour — press/link handling, reason-code presentation and the
 * alert-overlay policy stay with the platform adapters, which still differ.
 */
export const DashboardTile: React.FC<DashboardTileProps> = ({
    status,
    border,
    interactive,
    filled,
    title,
    footer,
    overlay,
    contentClassName,
    className,
    style,
    testID,
    children,
}) => (
    <Box
        testID={testID}
        style={style}
        className={twMerge(
            dashboardTileVariants({ status, border, interactive, filled }),
            className,
        )}
    >
        {title}
        <Box className={twMerge('flex-1 flex-col', contentClassName)}>{children}</Box>
        {footer}
        {overlay}
    </Box>
)

DashboardTile.displayName = 'DashboardTile'
